import { ReactNode } from 'react';
import { AlertCircle, Lock, Crown } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { getPlanDisplayName } from '../utils/permissions';

interface PermissionGuardProps {
  children: ReactNode;
  requires: 'core' | 'modules_metiers' | 'automated_reports' | 'daf_space' | 'associations' | 'agri' | 'pme';
  fallback?: ReactNode;
}

export function PermissionGuard({ children, requires, fallback }: PermissionGuardProps) {
  const { permissions, userPlan, loading } = usePermissions();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!permissions) {
    return fallback || <AccessDenied message="Vous devez être connecté pour accéder à cette fonctionnalité." />;
  }

  let hasAccess = false;

  switch (requires) {
    case 'core':
      hasAccess = permissions.access_core;
      break;
    case 'modules_metiers':
      hasAccess = permissions.access_modules_metiers;
      break;
    case 'automated_reports':
      hasAccess = permissions.access_automated_reports;
      break;
    case 'daf_space':
      hasAccess = permissions.access_daf_space;
      break;
    case 'associations':
      hasAccess = permissions.can_manage_subventions;
      break;
    case 'agri':
      hasAccess = permissions.can_manage_agri;
      break;
    case 'pme':
      hasAccess = permissions.can_manage_pme;
      break;
  }

  if (!hasAccess) {
    return fallback || <AccessDenied requires={requires} currentPlan={userPlan?.current_plan || null} />;
  }

  return <>{children}</>;
}

interface AccessDeniedProps {
  requires?: string;
  currentPlan?: string | null;
  message?: string;
}

function AccessDenied({ requires, currentPlan, message }: AccessDeniedProps) {
  const getRequiredPlan = (feature: string) => {
    switch (feature) {
      case 'modules_metiers':
        return 'Pack Métier ou supérieur';
      case 'automated_reports':
        return 'Pack Pilotage ou supérieur';
      case 'daf_space':
        return 'Pack DAF';
      case 'associations':
      case 'agri':
      case 'pme':
        return 'Pack Métier ou supérieur avec module sélectionné';
      default:
        return 'Un abonnement actif';
    }
  };

  const getIcon = () => {
    if (requires === 'daf_space') {
      return <Crown className="h-12 w-12 text-orange-500 mx-auto mb-4" />;
    }
    return <Lock className="h-12 w-12 text-orange-500 mx-auto mb-4" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <div className="text-center max-w-md mx-auto">
        {getIcon()}
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Fonctionnalité non disponible
        </h3>
        <p className="text-gray-600 mb-6">
          {message || `Cette fonctionnalité nécessite ${getRequiredPlan(requires || '')}.`}
        </p>
        {currentPlan && (
          <p className="text-sm text-gray-500 mb-4">
            Plan actuel : <span className="font-semibold">{getPlanDisplayName(currentPlan as any)}</span>
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/tarifs"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
          >
            Voir les offres
          </a>
          <a
            href="/app/account"
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition-colors"
          >
            Mon compte
          </a>
        </div>
      </div>
    </div>
  );
}
